var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { getDisabledElementProps, getHrefValue, getLinkProps, HawkinsPropsOverrideContext, mergeProps, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { usePress } from '../hooks';
import { useTextLinkStyles } from './textLink.styles';
var displayName = 'TextLink';
var overrideProps = {
    Icon: { size: 'small' },
    ThirdPartyIcon: { size: 'small' },
};
/**
 * Text links take a user to a destination or change contextual content on a page.
 */
export var TextLink = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), _c = _b.bold, bold = _c === void 0 ? false : _c, classesProp = _b.classes, className = _b.className, _d = _b.component, Component = _d === void 0 ? 'a' : _d, _e = _b.disabled, disabled = _e === void 0 ? false : _e, href = _b.href, layout = _b.layout, _f = _b.noWrap, noWrap = _f === void 0 ? false : _f, onClick = _b.onClick, rootProps = _b.rootProps, _g = _b.selected, selected = _g === void 0 ? false : _g, styleProp = _b.style, to = _b.to, _h = _b.variant, variant = _h === void 0 ? 'normal' : _h, restProps = __rest(_b, ["bold", "classes", "className", "component", "disabled", "href", "layout", "noWrap", "onClick", "rootProps", "selected", "style", "to", "variant"]);
    // Some articles suggest removing the `href` attribute to disable a link
    // (see https://www.scottohara.me/blog/2021/05/28/disabled-links.html),
    // but while that would work for normal anchor tags, it wouldn't work for React Router, which requires a `to` prop.
    // Since React Router is commonly used, we want to be able to handle that use case, so we programmatically disable the link.
    var pressProps = usePress({ alwaysHandleClick: disabled, disabled: disabled, onPress: onClick }).pressProps;
    var hasClickHandler = !!onClick;
    var isNormal = variant === 'normal';
    var isStandalone = variant === 'standalone';
    // Styles
    var baseClasses = useTextLinkStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root, (_a = {},
        // Libraries like React Router can automatically set a CSS class on the currently selected link.
        // Users can use the `HawkinsGlobalClass.SELECTED` value for that purpose.
        _a[HawkinsGlobalClass.SELECTED] = selected,
        _a[HawkinsGlobalClass.TRUNCATE] = noWrap,
        _a[classes.active] = selected,
        _a[classes.rootBold] = bold,
        _a[classes.rootDisabled] = disabled,
        _a[classes.rootEnabled] = !disabled,
        _a[classes.rootNormal] = isNormal,
        _a[classes.rootNormalDisabled] = isNormal && disabled,
        _a[classes.rootNoWrap] = noWrap,
        _a[classes.rootStandalone] = isStandalone,
        _a[classes.rootStandaloneDisabled] = isStandalone && disabled,
        _a));
    return (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: overrideProps }, { children: _jsx(Component, __assign({ className: rootClassName, ref: ref, 
            // An anchor tag without an `href` attribute isn't technically a link,
            // so explicitly set the ARIA role if it has a click handler so screen readers can be aware of it.
            // It is up to the consumer to override this with `role="button"` if they are using this component to perform a button action.
            role: getHrefValue({ href: href, to: to }) === null && hasClickHandler ? 'link' : undefined, style: style, 
            // In case the link has a click handler but no `href` attribute
            // (the browser won't make the anchor tag focusable if it has no `href`).
            tabIndex: hasClickHandler ? 0 : undefined }, getDisabledElementProps(Component, disabled), getLinkProps(Component, { href: href, to: to }), mergeProps(__assign(__assign({}, restProps), rootProps), pressProps))) })));
});
TextLink.displayName = displayName;
